import { learnerReturnPath } from 'modules/auth/return-path';
import React, { useState, useEffect } from 'react';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { useNavigate, useLocation } from 'react-router-dom';
import { User, ArrowRight, ArrowLeft, Check, Clock, Gift, Loader2 } from 'lucide-react';
import { LogoMark } from 'components/global/LogoMark';
import { useScrollTopOnChange } from 'hooks/use-scroll-top-on-change';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { useCompleteLearnerOnboarding } from 'modules/auth';
import { useAuthContext } from 'modules/auth/context';
import { useToast } from 'components/ui/use-toast';
import { PlanCheckoutModal } from 'components/portal/PlanCheckoutModal';
import { formatDateLong, formatDeadline, formatGroupedNumber } from 'lib/dateFormat';
import { useWelcomeBonus } from 'modules/portal-plans/useWelcomeBonus';
import {
  AGE_RANGES,
  CUSTOM_TIMEFRAME,
  EXAMS,
  MAX_GOALS,
  MINUTE_OPTIONS,
  answersComplete,
  dateComplete,
  goalRequest,
  toggleExam,
  type GoalDraft,
} from 'modules/studyPlan';
import { ChoiceCard } from './learner-onboarding/ChoiceCard';
import { DatesStep } from './learner-onboarding/DatesStep';
import { GoalStep } from './learner-onboarding/GoalStep';
import { EXAM_ICON, MINUTES_ICON, ageKeys, examKeys, levelKeys, minutesKeys, subjectKey, timeframeKeys } from './learner-onboarding/options';

type Step =
  | { kind: 'name' }
  | { kind: 'age' }
  | { kind: 'exams' }
  | { kind: 'goal'; exam: string }
  | { kind: 'minutes' }
  | { kind: 'dates' }
  | { kind: 'done' };

/** The questions follow the exams chosen: one screen for each, in the order they were picked. */
const stepsFor = (drafts: GoalDraft[]): Step[] => [
  { kind: 'name' },
  { kind: 'age' },
  { kind: 'exams' },
  ...drafts.map((draft): Step => ({ kind: 'goal', exam: draft.exam })),
  { kind: 'minutes' },
  { kind: 'dates' },
  { kind: 'done' },
];

/** An exam's display name; an id with no wording shows as itself. */
const examLabel = (t: TFunction, exam: string): string => t(examKeys(exam).label, { defaultValue: exam });

/** A goal's answers in one line: its grade, level or category, then its subjects. */
function answersLine(draft: GoalDraft, t: TFunction): string {
  const parts = [
    draft.level ? t(levelKeys(draft.exam, draft.level).label, { defaultValue: draft.level }) : '',
    draft.subjects.map((subject) => t(subjectKey(draft.exam, subject), { defaultValue: subject })).join(', '),
  ];
  return parts.filter(Boolean).join(' · ');
}

const whenLine = (draft: GoalDraft, t: TFunction): string =>
  draft.timeframe === CUSTOM_TIMEFRAME ? formatDateLong(`${draft.examDate}T12:00:00`) : t(timeframeKeys(draft.timeframe).label, { defaultValue: '' });

function Heading({ title, sub }: { title: string; sub?: string }) {
  return (
    <div className="text-center">
      <h1 className="text-2xl font-black tracking-tight sm:text-3xl">{title}</h1>
      {sub && <p className="mt-2 text-sm text-neutral-500">{sub}</p>}
    </div>
  );
}

export const LearnerOnboardingWizard: React.FC = () => {
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const user = state.user;
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const completeLearner = useCompleteLearnerOnboarding();
  const welcomeBonus = useWelcomeBonus();

  const queryParams = new URLSearchParams(location.search);
  const returnTo = learnerReturnPath(queryParams.get('returnTo'));

  // Form State
  const [stepIndex, setStepIndex] = useState(0);
  const [name, setName] = useState(user?.name && !user.name.includes('*') ? user.name : '');
  const [ageRange, setAgeRange] = useState('');
  const [drafts, setDrafts] = useState<GoalDraft[]>([]);
  const [dailyMinutes, setDailyMinutes] = useState(60);
  const [isPlanModalOpen, setIsPlanModalOpen] = useState(false);
  const [profileSaved, setProfileSaved] = useState(false);
  const [pendingAction, setPendingAction] = useState<'subscription' | 'free' | null>(null);
  const isSaving = pendingAction !== null || completeLearner.isPending;

  const steps = stepsFor(drafts);
  const step = steps[Math.min(stepIndex, steps.length - 1)];
  const questionCount = steps.length - 1;
  const updateDraft = (next: GoalDraft) => setDrafts((current) => current.map((draft) => (draft.exam === next.exam ? next : draft)));

  // Each step opens at its heading, not where the previous step was scrolled.
  useScrollTopOnChange(stepIndex);

  // Keep name synced if user loaded asynchronously
  useEffect(() => {
    const incomingName = user?.name;
    if (incomingName && !incomingName.includes('*')) {
      setName(current => current || incomingName);
    }
  }, [user?.name]);

  const stepIsAnswered = (current: Step): boolean => {
    switch (current.kind) {
      case 'name':
        return name.trim().length >= 2;
      case 'age':
        return ageRange !== '';
      case 'exams':
        return drafts.length > 0;
      case 'goal': {
        const draft = drafts.find((d) => d.exam === current.exam);
        return !!draft && answersComplete(draft);
      }
      case 'dates':
        return drafts.every((draft) => dateComplete(draft));
      default:
        return true;
    }
  };

  const nextStep = () => {
    if (!stepIsAnswered(step)) return;
    setStepIndex((index) => Math.min(steps.length - 1, index + 1));
  };
  const prevStep = () => {
    setProfileSaved(false);
    setStepIndex((index) => Math.max(0, index - 1));
  };

  const persistProfile = async (): Promise<boolean> => {
    if (profileSaved) return true;
    if (name.trim().length < 2 || !ageRange || drafts.length === 0 || !drafts.every((d) => answersComplete(d) && dateComplete(d))) {
      toast({ title: t('learnerOb.toast_incomplete'), variant: 'destructive' });
      return false;
    }
    try {
      await completeLearner.mutateAsync({
        name: name.trim(),
        ageRange,
        dailyStudyMinutes: dailyMinutes,
        goals: drafts.map(goalRequest),
      });
      setProfileSaved(true);
      return true;
    } catch (err: unknown) {
      toast({
        title: t('learnerOb.toast_error'),
        description: (isAxiosError<{ message?: string }>(err) && err.response?.data?.message) || t('learnerOb.toast_save_failed'),
        variant: 'destructive',
      });
      return false;
    }
  };

  const handleGetSubscriptionPlan = async () => {
    if (isSaving) return;
    setPendingAction('subscription');
    const ok = await persistProfile();
    setPendingAction(null);
    if (ok) {
      setIsPlanModalOpen(true);
    }
  };

  const handleContinueFree = async () => {
    if (isSaving) return;
    setPendingAction('free');
    const ok = await persistProfile();
    setPendingAction(null);
    if (!ok) return;

    toast({
      title: t('learnerOb.toast_welcome'),
      description: t('learnerOb.toast_welcome_desc'),
    });

    // Choosing free dismisses offers carried from landing or earlier onboarding.
    const target = new URL(returnTo ?? '/portal', window.location.origin);
    target.searchParams.delete('plan');
    target.searchParams.delete('suggestPlan');
    try {
      sessionStorage.removeItem('suggest_plan_onboarding');
      if (welcomeBonus) sessionStorage.setItem('onboarding_welcome_bonus', 'true');
    } catch {
      // Storage can be unavailable; the URL still honors the free choice.
    }
    navigate(`${target.pathname}${target.search}${target.hash}`, { replace: true });
  };

  const isDone = step.kind === 'done';
  const progressPercent = Math.min(100, Math.round((stepIndex / questionCount) * 100));
  const goalIndex = step.kind === 'goal' ? drafts.findIndex((d) => d.exam === step.exam) : -1;

  const continueButton = (
    <div className="flex justify-end pt-2">
      <Button
        type="button"
        disabled={!stepIsAnswered(step)}
        onClick={nextStep}
        className="h-12 w-full bg-neutral-950 text-sm font-bold text-white hover:bg-neutral-800 dark:bg-white dark:text-neutral-950 sm:w-auto sm:px-8"
      >
        <span>{t('learnerOb.continue')}</span>
        <ArrowRight className="ml-2 h-4 w-4" />
      </Button>
    </div>
  );

  return (
    <div className="flex min-h-screen w-full flex-col bg-background text-foreground">
      {/* Top Header & Progress */}
      <div className="sticky top-0 z-20 border-b border-border bg-background/80 backdrop-blur-md">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-3 sm:px-6">
          <div className="flex items-center gap-2">
            <LogoMark size={24} />
            <span className="font-display text-sm font-extrabold tracking-[-0.02em]">Examing</span>
          </div>

          <div className="flex items-center gap-3">
            {stepIndex > 0 && (
              <button
                type="button"
                onClick={prevStep}
                disabled={isSaving}
                className="flex items-center gap-1 text-xs font-semibold text-neutral-500 transition hover:text-neutral-900 dark:hover:text-white"
              >
                <ArrowLeft className="h-3.5 w-3.5" />
                <span>{t('learnerOb.back')}</span>
              </button>
            )}
            {!isDone && (
              <span className="text-xs font-bold text-neutral-400">
                {t('learnerOb.step_of', { current: stepIndex + 1, total: questionCount })}
              </span>
            )}
          </div>
        </div>

        {/* Smooth animated progress line */}
        {!isDone && (
          <div className="h-1 w-full bg-neutral-100 dark:bg-neutral-800">
            <div
              className="h-full bg-[#10b981] transition-[width] duration-300 ease-out"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        )}
      </div>

      {/* Center Wizard Body */}
      <main className="flex flex-1 items-center justify-center p-4 sm:p-8">
        <div className="w-full max-w-xl">
          {step.kind === 'name' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-3 duration-200">
              <div className="text-center">
                <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-muted text-muted-foreground">
                  <User className="h-6 w-6" />
                </div>
                <h1 className="text-2xl font-black tracking-tight sm:text-3xl">{t('learnerOb.name_title')}</h1>
                <p className="mt-2 text-sm text-neutral-500">
                  {t('learnerOb.name_sub')}
                </p>
              </div>

              <Input
                autoFocus
                type="text"
                aria-label={t('learnerOb.name_aria')}
                placeholder={t('learnerOb.name_placeholder')}
                value={name}
                onChange={(e) => setName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') nextStep();
                }}
                className="h-14 border-neutral-200 bg-white px-5 text-base font-semibold shadow-sm dark:border-neutral-800 dark:bg-neutral-900"
              />
              {continueButton}
            </div>
          )}

          {step.kind === 'age' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-3 duration-200">
              <Heading title={t('learnerOb.age_title')} sub={t('learnerOb.age_sub')} />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {AGE_RANGES.map((age) => {
                  const keys = ageKeys(age);
                  return <ChoiceCard key={age} label={t(keys.label)} sub={keys.sub && t(keys.sub)} selected={ageRange === age} onSelect={() => setAgeRange(age)} />;
                })}
              </div>
              {continueButton}
            </div>
          )}

          {step.kind === 'exams' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-3 duration-200">
              <Heading title={t('learnerOb.exams_title')} sub={t('learnerOb.exams_sub', { count: MAX_GOALS })} />
              <div className="space-y-3">
                {EXAMS.map((exam) => {
                  const selected = drafts.some((d) => d.exam === exam);
                  const keys = examKeys(exam);
                  return (
                    <ChoiceCard
                      key={exam}
                      label={t(keys.label)}
                      sub={keys.sub && t(keys.sub)}
                      icon={EXAM_ICON[exam]}
                      multiple
                      selected={selected}
                      disabled={!selected && drafts.length >= MAX_GOALS}
                      onSelect={() => setDrafts((current) => toggleExam(current, exam))}
                    />
                  );
                })}
              </div>
              {continueButton}
            </div>
          )}

          {step.kind === 'goal' && goalIndex >= 0 && (
            <div key={step.exam} className="space-y-6 animate-in fade-in slide-in-from-bottom-3 duration-200">
              <Heading title={examLabel(t, step.exam)} sub={drafts.length > 1 ? t('learnerOb.goal_position', { index: goalIndex + 1, total: drafts.length }) : undefined} />
              <GoalStep draft={drafts[goalIndex]} onChange={updateDraft} />
              {continueButton}
            </div>
          )}

          {step.kind === 'minutes' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-3 duration-200">
              <Heading
                title={t('learnerOb.minutes_title')}
                sub={drafts.length > 1 ? t('learnerOb.minutes_sub_many') : t('learnerOb.minutes_sub_one')}
              />
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {MINUTE_OPTIONS.map((minutes) => {
                  const keys = minutesKeys(minutes);
                  return (
                    <ChoiceCard
                      key={minutes}
                      label={t(keys.label)}
                      sub={keys.sub && t(keys.sub)}
                      icon={MINUTES_ICON[minutes]}
                      selected={dailyMinutes === minutes}
                      onSelect={() => setDailyMinutes(minutes)}
                    />
                  );
                })}
              </div>
              {continueButton}
            </div>
          )}

          {step.kind === 'dates' && (
            <div className="space-y-6 animate-in fade-in slide-in-from-bottom-3 duration-200">
              <Heading
                title={drafts.length > 1 ? t('learnerOb.dates_title_many') : t('learnerOb.dates_title_one')}
                sub={t('learnerOb.dates_sub')}
              />
              <DatesStep drafts={drafts} onChange={updateDraft} />
              {continueButton}
            </div>
          )}

          {isDone && (
            <div className="space-y-6 text-center animate-in zoom-in-95 fade-in duration-300">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-400">
                <Check className="h-7 w-7" strokeWidth={2.25} aria-hidden />
              </div>

              <div>
                <h1 className="text-2xl font-black tracking-tight sm:text-3xl text-neutral-900 dark:text-white">
                  {t('learnerOb.done_title', { name })}
                </h1>
                <p className="mt-1 text-sm font-medium text-success-700 dark:text-success-400">
                  {t('learnerOb.done_sub')}
                </p>
              </div>

              {/* Summary Card */}
              <div className="rounded-lg border border-border bg-card p-6 text-left shadow-sm">
                <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-400">
                  {t('learnerOb.plan_params')}
                </h3>
                <ul className="mt-4 space-y-3 text-xs">
                  {drafts.map((draft) => (
                    <li key={draft.exam} className="flex items-start justify-between gap-4 border-b border-neutral-100 pb-2.5 dark:border-neutral-800">
                      <span className="min-w-0">
                        <strong className="block font-bold text-neutral-900 dark:text-white">{examLabel(t, draft.exam)}</strong>
                        {answersLine(draft, t) && <span className="block text-neutral-500">{answersLine(draft, t)}</span>}
                      </span>
                      <span className="shrink-0 text-right font-semibold text-neutral-900 dark:text-white">{whenLine(draft, t)}</span>
                    </li>
                  ))}
                  <li className="flex items-center justify-between">
                    <span className="text-neutral-500">{t('learnerOb.daily_label')}</span>
                    <strong className="font-bold text-neutral-900 dark:text-white">{t('learnerOb.daily_value', { minutes: dailyMinutes })}</strong>
                  </li>
                </ul>
                {dailyMinutes === MINUTE_OPTIONS[0] && drafts.length > 1 && (
                  <p className="mt-4 rounded-md bg-muted px-3 py-2 text-xs leading-relaxed text-muted-foreground">
                    {t('learnerOb.thin_plan', { count: drafts.length })}
                  </p>
                )}
              </div>

              {/* Welcome bonus: only while admins have it on and it has not ended */}
              {welcomeBonus && (
                <div className="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50/80 p-4 text-left dark:border-emerald-900/40 dark:bg-emerald-950/40">
                  <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md bg-emerald-500 text-white">
                    <Gift className="h-5 w-5" />
                  </div>
                  <div className="min-w-0">
                    <div className="text-sm font-extrabold text-emerald-900 dark:text-emerald-300">
                      {t('learnerOb.bonus_title', { amount: formatGroupedNumber(welcomeBonus.amountSom) })}
                    </div>
                    <div className="text-xs text-emerald-700 dark:text-emerald-400">
                      {t('learnerOb.bonus_desc')}
                    </div>
                    {welcomeBonus.endsAt && (
                      <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold text-amber-900 dark:bg-amber-500/15 dark:text-amber-300">
                        <Clock className="h-3.5 w-3.5" aria-hidden />
                        <span>{t('learnerOb.bonus_ends', { when: formatDeadline(welcomeBonus.endsAt) })}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="space-y-3 pt-2">
                <Button
                  type="button"
                  disabled={isSaving}
                  aria-busy={pendingAction === 'subscription'}
                  aria-haspopup="dialog"
                  onClick={handleGetSubscriptionPlan}
                  className="h-14 w-full gap-2 text-base font-extrabold"
                >
                  {pendingAction === 'subscription' && <Loader2 className="h-5 w-5 animate-spin" aria-hidden />}
                  <span>{t('learnerOb.subscribe')}</span>
                </Button>

                <Button
                  type="button"
                  variant="ghost"
                  disabled={isSaving}
                  aria-busy={pendingAction === 'free'}
                  onClick={handleContinueFree}
                  className="h-12 w-full gap-2 text-sm font-bold text-neutral-600 transition-colors hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-400 dark:hover:bg-neutral-800 dark:hover:text-white"
                >
                  {pendingAction === 'free' && (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                  )}
                  <span>{t('learnerOb.continue_free')}</span>
                </Button>
              </div>
            </div>
          )}
        </div>
      </main>

      {isPlanModalOpen && <PlanCheckoutModal
        open={isPlanModalOpen}
        onOpenChange={(nextOpen) => {
          setIsPlanModalOpen(nextOpen);
          if (!nextOpen && profileSaved) {
            try {
              if (welcomeBonus) sessionStorage.setItem('onboarding_welcome_bonus', 'true');
            } catch {
              // ignore
            }
            const target = new URL(returnTo ?? '/portal', window.location.origin);
            target.searchParams.delete('plan');
            target.searchParams.delete('suggestPlan');
            navigate(`${target.pathname}${target.search}${target.hash}`, { replace: true });
          }
        }}
        onboardingOffer
      />}
    </div>
  );
};

export default LearnerOnboardingWizard;
